import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Shield, 
  ArrowUpRight, 
  Globe, 
  Layers, 
  Database, 
  Sparkles, 
  Paintbrush, 
  TrendingUp, 
  Coins, 
  CheckSquare, 
  Square, 
  ChevronDown, 
  ChevronUp, 
  RefreshCw,
  Activity,
  Cpu,
  Search,
  LogOut,
  LogIn,
  Clock,
  Menu,
  X,
  Share2,
  Wand2
} from 'lucide-react';
import { CATEGORIES } from '../data';
import { NexoraXQuickFilter } from './NexoraXQuickFilter';
import { useAuth } from '../lib/AuthContext';

interface NexoraXHeaderNavigationProps {
  onLocalTabChange: (tabName: string | null) => void;
  activeTab: string | null;
  onSelectCategory?: (categoryTitle: string, subNode?: string | null) => void;
  user?: any;
  handleHeaderLogout?: () => Promise<void>;
  globalSearch?: string;
  setGlobalSearch?: (str: string) => void;
  searchResults?: any[];
  isSearchFocused?: boolean;
  setIsSearchFocused?: (b: boolean) => void;
  handleSearchResultClick?: (result: any) => void;
  currentTime?: Date;
  onShowGovCorpGateway?: () => void;
  companyDivisions?: any[];
  isVaultUnlocked?: boolean;
}

interface InfrastructureNode {
  id: string;
  code: string;
  name: string;
  baseVal: number;
  completed: boolean;
}

export const NexoraXHeaderNavigation: React.FC<NexoraXHeaderNavigationProps> = ({ 
  onLocalTabChange,
  activeTab,
  onSelectCategory,
  user,
  handleHeaderLogout,
  globalSearch = "",
  setGlobalSearch,
  searchResults = [],
  isSearchFocused = false,
  setIsSearchFocused,
  handleSearchResultClick,
  currentTime,
  onShowGovCorpGateway,
  companyDivisions = [],
  isVaultUnlocked = false
}) => {
  const { user: authUser } = useAuth();
  const isDirector = useMemo(() => {
    const email = authUser?.email?.toLowerCase().trim();
    return email === "siddique.mahmood@gmail.com" || email?.includes("nexorax") || sessionStorage.getItem("nexorax_mfa_verified") === "true";
  }, [authUser]);

  const VISIBLE_PUBLIC_PRODUCT_CATEGORIES = [
  {
    id: 201,
    title: "NexoraX Search Engine",
    nodes: ["Site Search Engine", "Google Visibility Search", "Product Search", "Lead Search", "Knowledge Base Search"],
  },
  {
    id: 202,
    title: "NexoraX Hosting",
    nodes: ["Business Hosting", "Domain Setup", "Business Email", "SSL Setup", "Server Monitoring"],
  },
  {
    id: 203,
    title: "AI Automation",
    nodes: ["AI Sales Agent", "Workflow Automation", "Document Processing AI", "Voice Assistant", "Autonomous Agent Swarm"],
  },
  {
    id: 204,
    title: "Websites & Apps",
    nodes: ["Business Website", "Web App", "Mobile App", "Product Builder", "Customer Portal"],
  },
  {
    id: 205,
    title: "Sales Agents",
    nodes: ["Lead Finder", "Deal Closer", "Vanguard Closer", "Hermes Outreach", "Client Deal Registry"],
  },
  {
    id: 206,
    title: "Deliver & Driver Operations",
    nodes: ["Courier Dispatch", "Driver Route Planner", "Delivery Tracking", "Proof Of Delivery", "Campaign Assets"],
  },
  {
    id: 207,
    title: "QR Engine",
    nodes: ["QR Campaigns", "Sticker QR", "Menu QR", "Tracking QR", "Lead Capture QR"],
  },
  {
    id: 208,
    title: "Code Engine",
    nodes: ["Code Generator", "Program Engine", "API Builder", "Automation Scripts", "Quality Assurance AI"],
  },
  {
    id: 209,
    title: "SEO & Growth",
    nodes: ["SEO Pages", "Google Visibility", "Trend Scout", "Offer Ladder", "Organic Growth Engine"],
  },
  {
    id: 210,
    title: "Media & Branding",
    nodes: ["Logo Pack", "Video Generator", "Social Media Kit", "Brand Identity", "Content Studio"],
  },
  {
    id: 211,
    title: "CRM / Dashboards",
    nodes: ["CRM Dashboard", "Lead Pipeline", "Client Portal", "Invoice Register", "Performance Reports"],
  },
];

const marketplaceExternalUrl = "/marketplace"; 
  const [isAdvancedConsoleOpen, setIsAdvancedConsoleOpen] = useState(false);
  const [isMatrixOpen, setIsMatrixOpen] = useState(false);
  const [isNodesDropdownOpen, setIsNodesDropdownOpen] = useState(false);
  const [isCapitalFlowOpen, setIsCapitalFlowOpen] = useState(false);
  const [isHealthOpen, setIsHealthOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedNodeName, setSelectedNodeName] = useState<string | null>(null);
  const visibleCategoryTabs = [
    ...VISIBLE_PUBLIC_PRODUCT_CATEGORIES,
    ...CATEGORIES.filter(category => !VISIBLE_PUBLIC_PRODUCT_CATEGORIES.some(publicCategory => publicCategory.title === category.title)),
  ];

  

  // Accordion active group state for node matrix dropdown
  const [activeNodeGroup, setActiveNodeGroup] = useState<string | null>(null);

  // Live Telemetry diagnostics
  const [activeSubnodes, setActiveSubnodes] = useState<number>(108);
  const [executionVolume, setExecutionVolume] = useState<number>(192804);
  const [cpuLoad, setCpuLoad] = useState<number>(31.2);
  const [coreTemp, setCoreTemp] = useState<number>(41.5);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSubnodes(prev => {
        const delta = Math.random() > 0.6 ? 1 : Math.random() < 0.4 ? -1 : 0;
        const next = prev + delta;
        return Math.max(90, Math.min(112, next));
      });
      setExecutionVolume(prev => prev + Math.floor(Math.random() * 5) + 1);
      setCpuLoad(prev => {
        const shift = (Math.random() - 0.5) * 4;
        const next = prev + shift;
        return parseFloat(Math.max(15.0, Math.min(60.0, next)).toFixed(1));
      });
      setCoreTemp(prev => {
        const shift = (Math.random() - 0.5) * 0.8;
        const next = prev + shift;
        return parseFloat(Math.max(38.0, Math.min(46.0, next)).toFixed(1));
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const [nodes, setNodes] = useState<InfrastructureNode[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('nexorax-infra-nodes');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error("Failed parsing Nexus Nodes ledger", e);
        }
      }
    }
    return [
      { id: 'node-01', code: 'Node-01', name: 'Vanguard-Closer Core', baseVal: 1250000, completed: true },
      { id: 'node-06', code: 'Node-06', name: 'Oracle Speech Engine', baseVal: 850000, completed: true },
      { id: 'node-07', code: 'Node-07', name: 'Quantum Billing Matrix', baseVal: 950500, completed: true },
      { id: 'node-09.2', code: 'Node-09.2', name: 'Antigravity Core Upgrade', baseVal: 1500000, completed: true },
      { id: 'node-16', code: 'Node-16', name: 'Compliance Gateway Arbiter', baseVal: 600000, completed: false },
      { id: 'node-17', code: 'Node-17', name: 'Chronos NexoraX Ledger', baseVal: 2227800, completed: true },
      { id: 'node-18.5', code: 'Node-18.5', name: 'Secretary-Node CoS Portal', baseVal: 750000, completed: false },
    ];
  });

  useEffect(() => {
    localStorage.setItem('nexorax-infra-nodes', JSON.stringify(nodes));
    window.dispatchEvent(new CustomEvent('nexorax-infra-nodes-changed', { detail: nodes }));
  }, [nodes]);

  const [theme, setTheme] = useState<'cyan' | 'amber'>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('nexorax-theme') as 'cyan' | 'amber') || 'cyan';
    }
    return 'cyan';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'amber') {
      root.setAttribute('data-theme', 'amber');
    } else {
      root.removeAttribute('data-theme');
    }
    localStorage.setItem('nexorax-theme', theme);
    window.dispatchEvent(new CustomEvent('nexorax-theme-changed', { detail: theme }));
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'cyan' ? 'amber' : 'cyan');
  };

  const consoleRef = useRef<HTMLDivElement>(null);
  const matrixDropdownRef = useRef<HTMLDivElement>(null);

  // Escaping scroll downs, clicks outside and Escape key presses
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsAdvancedConsoleOpen(false);
        setIsMatrixOpen(false);
        setIsNodesDropdownOpen(false);
        setIsCapitalFlowOpen(false);
        setIsHealthOpen(false);
      }
    };
    const handleScroll = () => {
      setIsAdvancedConsoleOpen(false);
      setIsMatrixOpen(false);
      setIsNodesDropdownOpen(false);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (consoleRef.current && !consoleRef.current.contains(e.target as Node)) {
        setIsAdvancedConsoleOpen(false);
      }
      if (matrixDropdownRef.current && !matrixDropdownRef.current.contains(e.target as Node)) {
        setIsNodesDropdownOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const totalGrossHarvest = useMemo(() => {
    return nodes.filter(n => n.completed).reduce((sum, n) => sum + n.baseVal, 0);
  }, [nodes]);

  const NexoraXPerpetualLevy = useMemo(() => totalGrossHarvest * 0.03, [totalGrossHarvest]);
  const remainingAfterLevy = useMemo(() => totalGrossHarvest - NexoraXPerpetualLevy, [totalGrossHarvest, NexoraXPerpetualLevy]);
  const lifetimeTaxGavelFee = useMemo(() => remainingAfterLevy * 0.10, [remainingAfterLevy]);
  const netfinanceSettlement = useMemo(() => remainingAfterLevy - lifetimeTaxGavelFee, [remainingAfterLevy, lifetimeTaxGavelFee]);

  const charity1Split = useMemo(() => NexoraXPerpetualLevy * 0.3333, [NexoraXPerpetualLevy]);
  const charity2Split = useMemo(() => NexoraXPerpetualLevy * 0.3333, [NexoraXPerpetualLevy]);
  const charity3Split = useMemo(() => NexoraXPerpetualLevy * 0.3334, [NexoraXPerpetualLevy]);

  const mapCategoryItemToTabName = (label: string): string => {
    switch (label) {
      case "NexoraX Ecosystem": return "Ecosystem";
      case "Nexus OS": return "Nexus OS";
      case "NexoraX Marketplace": return "NexoraX Marketplace";
      case "Client Dashboard": return "Nexus OS";
      case "Admin Control Hub": return "Admin Panel";
      case "Grant Funding Pack": return "Grant Funding Pack";
      case "NexoraX Advanced Console": return "NexoraX Advanced Console";
      case "Nexus Galaxy Lab": return "Nexus Galaxy Lab";
      
      case "NexoraX Chat": return "Nexorax Chat";
      case "Nexus Agent Automation": return "NexoraX AI Agent Automation";
      case "NexoraX AI Automation": return "NEXORAX CATEGORY 27 CONSOLE";
      case "Nex Node": return "NexoraX Hermes Node";
      case "Nexus Voice Matrix": return "NexoraX Voice";
      case "NexoraX Voice Engine": return "NexoraX Voice";
      
      case "NexoraX Media Studio": return "NexoraX Media Generation Studio";
      case "AI Ad Factory & UGC": return "AI Ad Factory & UGC Creator Studio";
      case "Social Media Automation": return "Social Media Automation";
      case "NexoraX Studio": return "Nexorax Studio";
      case "Guides & Videos": return "Guides & Videos";
      case "Latest News": return "Latest News";
      case "Updates": return "Updates";
      
      case "Nexus Search Engine": return "NexoraX Multiverse Search Engine";
      case "NexoraX Hosting": return "NexoraX Hosting";
      case "Mail & Domains Platform": return "Mail & Domains Platform";
      case "NexoraX Workplace Suite": return "NexoraX Workspace Suite";
      case "Driver / Courier / Fleet Systems": return "Driver / Courier / Fleet Systems";
      case "Services & Solutions": return "Services / Solutions";
      case "Payment Tiers": return "Tiers";
      case "Contact": return "Contact";
      case "Campaign Assets": return "Campaign Assets";
      
      case "Firebase Eclipse": return "NexoraX Backup Storage Blocks";
      case "Exchange Eclipse": return "Unified Mail Ingress Array";
      case "Cloudflare Eclipse": return "Domain Registry & Edge Tunnel Monitor";
      case "Metering Ledger": return "Nexus Accounting & Metering System";
      case "Secure Invoice Register": return "SECRETARY NODE";
      case "Nexus Premium Billing Ledger": return "Tiers";
      default: return label;
    }
  };

  const onNavItemClick = (itemTabName: string, subTab?: string) => {
    setIsAdvancedConsoleOpen(false);
    setIsMatrixOpen(false);
    setIsNodesDropdownOpen(false);
    setIsMobileMenuOpen(false);
    setIsCapitalFlowOpen(false);
    setIsHealthOpen(false);
    
    if (itemTabName === "Remote Workplace") {
      window.location.assign("https://workplace.nexoraxdigital.co.uk");
      return;
    }

    if (itemTabName === "LOG_OUT") {
      handleHeaderLogout?.();
    } else if (itemTabName === "Driver / Courier / Fleet Systems") {
      onLocalTabChange("Tiers");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('nexorax-trigger-driver-quote'));
      }, 150);
    } else if (itemTabName === "Campaign Hub") {
      sessionStorage.setItem('nexus_os_initial_subtab', 'CampaignAssets');
      onLocalTabChange("Nexus OS");
    } else if (itemTabName === "Campaign Assets") {
      sessionStorage.setItem('nexus_os_initial_subtab', 'CampaignAssets');
      onLocalTabChange("Nexus OS");
    } else if (itemTabName === "Deliver & Driver Operations" || itemTabName === "Driver Ops") {
      onLocalTabChange("Deliver & Driver Operations");
    } else if (itemTabName === "NexoraX Marketplace" || itemTabName === "Marketplace") {
      onLocalTabChange("NexoraX Marketplace");
    } else if (itemTabName === "Login / Join" || itemTabName === "Login" || itemTabName === "Join") {
      onLocalTabChange(itemTabName === "Login / Join" ? "Login" : itemTabName);
    } else {
      onLocalTabChange(itemTabName);
    }

    if (subTab) {
      window.dispatchEvent(new CustomEvent('nexorax-trigger-admin-subtab', { detail: subTab }));
    }

    // Scroll to content start cleanly below the sticky header (using smooth scroll)
    setTimeout(() => {
      const contentEl = document.querySelector('main');
      if (contentEl) {
        contentEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };



  const advancedCategories = useMemo(() => {
    const list = [
      {
        title: "Core Platform",
        items: [
          "NexoraX Ecosystem",
          "Nexus OS",
          "NexoraX Marketplace",
          "Client Dashboard",
          ...(isDirector ? ["Admin Control Hub", "Grant Funding Pack"] : []),
          "NexoraX Advanced Console"
        ]
      },
      {
        title: "AI & Automation",
        items: [
          "NexoraX Chat",
          "Nexus Agent Automation",
          "NexoraX AI Automation",
          "Nexus Galaxy Lab",
          "Nex Node",
          "Nexus Voice Matrix",
          "NexoraX Voice Engine"
        ]
      },
      {
        title: "Media & Growth",
        items: [
          "NexoraX Media Studio",
          "AI Ad Factory & UGC",
          "Social Media Automation",
          "NexoraX Studio",
          "Guides & Videos",
          "Latest News",
          "Updates"
        ]
      },
      {
        title: "Business Systems",
        items: [
          "Nexus Search Engine",
          "NexoraX Hosting",
          "Mail & Domains Platform",
          "NexoraX Workplace Suite",
          "Driver / Courier / Fleet Systems",
          "Services & Solutions",
          "Payment Tiers",
          "Contact"
        ]
      }
    ];

    if (isDirector) {
      list.push({
        title: "Technical / Admin Tools",
        items: [
          "Firebase Eclipse",
          "Exchange Eclipse",
          "Cloudflare Eclipse",
          "Metering Ledger",
          "Secure Invoice Register",
          "Nexus Premium Billing Ledger"
        ]
      });
    }
    return list;
  }, [isDirector]);

  // Handle click on node in matrix
  const handleNodeClick = (node: { name: string; categoryTitle: string; categoryId: number }) => {
    setIsNodesDropdownOpen(false);
    if (node.categoryTitle === "BUSINESS" && (node.name === "Logistics" || node.name === "Driver / Courier / Fleet Systems")) {
      setSelectedNodeName(null);
      onLocalTabChange("Tiers");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('nexorax-trigger-driver-quote'));
      }, 150);
      return;
    }
    setSelectedNodeName(node.name);
    onLocalTabChange(null);
    if (onSelectCategory) {
      onSelectCategory(node.categoryTitle, node.name);
    }
    setTimeout(() => {
      const contentEl = document.querySelector('main');
      if (contentEl) {
        contentEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <header className="w-full min-w-0 max-w-full box-border bg-[#02040a]/95 border-b border-cyan-500/15 text-white sticky top-0 z-50 backdrop-blur-3xl shadow-[0_10px_40px_rgba(0,0,0,0.85)] font-mono flex flex-col pointer-events-auto">
      {/* Subtle bottom glow separator */}
      <div className="absolute inset-x-0 bottom-0 h-[1.5px] bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent pointer-events-none"></div>

      {/* TASK 1: SIMPLE COMPACT STICKY PRIMARY HEADER (Target Height: 80-96px) */}
      <div className="flex items-center justify-between px-2 sm:px-6 py-3 min-h-[72px] lg:h-[84px] gap-2 lg:gap-4 w-full max-w-7xl min-w-0 box-border mx-auto">
        
        {/* Core Corporation Branding & Registrar */}
        <div className="flex items-center gap-2 lg:gap-4 min-w-0 flex-1 lg:flex-none lg:shrink-0">
          <button 
            onClick={() => onLocalTabChange(null)}
            className="flex items-center gap-2 lg:gap-3 min-w-0 max-w-full hover:opacity-85 transition-opacity duration-300 text-left cursor-pointer select-none"
          >
            <img 
              src="/logo.png?v=2" 
              alt="NexoraX Anchor" 
              className="h-10 w-auto"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="flex flex-col min-w-0 flex-1 leading-tight">
              <span className="block min-w-0 truncate text-[10px] sm:text-xs lg:text-[13px] font-black tracking-[0.05em] lg:tracking-[0.2em] text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-indigo-400 to-[#e840f3]">
                NEXORAX DIGITAL LTD
              </span>
              <span className="block min-w-0 truncate text-[7px] lg:text-[8px] text-slate-500 font-bold tracking-[0.04em] lg:tracking-[0.18em]">
                OFFICE OF THE DIRECTOR • SC887095
              </span>
            </div>
          </button>
        </div>

        <nav className="hidden lg:flex flex-1 min-w-0 items-center gap-1 overflow-x-auto whitespace-nowrap scroll-smooth custom-scrollbar">
          {isDirector ? (
            [
              { label: "Admin Home", tab: "Admin Panel", subTab: "SecurePayment_REVENUE" },
              { label: "Remote Workforce OS", tab: "Admin Panel", subTab: "WORKFORCE_OS" },
              { label: "Campaign Assets", tab: "Admin Panel", subTab: "REVENUE_COMMAND_CENTER" },
              { label: "Revenue Engine", tab: "Admin Panel", subTab: "SecurePayment_REVENUE" },
              { label: "Product Factory", tab: "Admin Panel", subTab: "DIGITAL_PRODUCT_FACTORY" },
              { label: "Security Logs", tab: "Admin Panel", subTab: "SECURITY_LOGS" },
              { label: "Log Out", tab: "LOG_OUT", isLogout: true }
            ].map((item) => {
              const isItemActive = activeTab === item.tab;
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => onNavItemClick(item.tab, item.subTab)}
                  className={`transition-all duration-200 cursor-pointer py-1.5 px-2.5 rounded-lg text-xs font-sans font-bold tracking-wide border ${
                    'isLogout' in item
                      ? "text-rose-400 bg-rose-955/25 border-rose-500/30 hover:bg-rose-900/45 hover:border-rose-400 shadow-[0_0_12px_rgba(244,63,94,0.15)] font-black uppercase"
                      : isItemActive
                        ? "text-purple-400 bg-purple-950/20 border-purple-500/35 shadow-[0_0_12px_rgba(168,85,247,0.15)]"
                        : "hover:text-white text-zinc-300 hover:bg-white/5 border-transparent bg-transparent"
                  }`}
                >
                  {item.label}
                </button>
              );
            })
          ) : (
            [
              { label: "Home", tab: "Home" },
              { label: "Driver Ops", tab: "Deliver & Driver Operations" },
              { label: "Revenue Engine", tab: "NexoraX Revenue Engine" },
              { label: "Services", tab: "Services / Solutions" },
              { label: "Marketplace", tab: "Marketplace" },
              { label: "Digital Products", tab: "Digital Products" },
              { label: "Product Builder", tab: "Product Builder" },
              { label: "My Projects", tab: "My Projects" },
              { label: "My Purchases", tab: "My Purchases" },
              { label: "Nexus OS", tab: "Nexus OS" },
              { label: "Operator Console", tab: "Operator Console" },
              { label: "Remote Workplace", tab: "Remote Workplace" },
              { label: "Smart Quote", tab: "Smart Quote" },
              { label: "Client Preview", tab: "Client Preview" },
              { label: "Showcase", tab: "Showcase" },
              { label: "QR Campaign", tab: "QR Campaign" },
              { label: "Launch Pad", tab: "Launch Pad" },
    { label: "Campaign Hub", tab: "Campaign Hub" },
              { label: "Guides & Videos", tab: "Guides & Videos" },
              { label: "Remote Training", tab: "Remote Training" },
              { label: "Pricing", tab: "Tiers" },
              { label: "Contact", tab: "Contact" },
              ...(authUser 
                ? [
                    { label: "Log Out", tab: "LOG_OUT", isLogout: true }
                  ] 
                : [
                    { label: "Login / Join", tab: "Login / Join" }
                  ]
              )
            ].map((item) => {
              const isItemActive = activeTab === item.tab || (item.tab === "Login / Join" && (activeTab === "Login" || activeTab === "Join"));
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => onNavItemClick(item.tab)}
                  className={`transition-all duration-200 cursor-pointer py-1.5 px-2.5 rounded-lg text-xs font-sans font-bold tracking-wide border ${
                    'isLogout' in item
                      ? "text-rose-400 bg-rose-955/25 border-rose-500/30 hover:bg-rose-900/45 hover:border-rose-400 shadow-[0_0_12px_rgba(244,63,94,0.15)] font-black uppercase"
                      : isItemActive 
                        ? "text-[#10b981] bg-[#10b981]/10 border-[#10b981]/35 shadow-[0_0_12px_rgba(16,185,129,0.15)]" 
                        : "hover:text-white text-zinc-300 hover:bg-white/5 border-transparent bg-transparent"
                  }`}
                >
                  {item.label}
                </button>
              );
            })
          )}
        </nav>

        {/* Desktop Advanced Console & Actions Drawer Side */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          
          {/* User status badge with active logout CTA */}
          {authUser && (
            <div className="flex items-center gap-2 bg-zinc-950/65 border border-zinc-800/40 rounded-lg p-1 px-2.5 max-h-[38px] transition-all">
              <div className="flex flex-col text-left mr-1">
                <span className="text-[7px] font-black tracking-widest text-[#00ffd2] uppercase font-mono">
                  {isDirector ? "NEX_DIRECTOR" : "NEX_CLIENT"}
                </span>
                <span className="text-[8.5px] text-zinc-400 font-bold max-w-[100px] truncate leading-none">
                  {authUser.email || "AUTHORIZED"}
                </span>
              </div>
              <button
                onClick={async () => {
                  await handleHeaderLogout?.();
                }}
                className="flex items-center gap-1 py-1 px-2 bg-rose-950/30 hover:bg-rose-900/40 border border-rose-500/20 hover:border-rose-500 text-rose-400 hover:text-white rounded text-[8px] font-black uppercase tracking-wider transition-all cursor-pointer"
                title="Disconnect Active Authentication Channel"
              >
                <LogOut className="w-2.5 h-2.5 text-rose-400 shrink-0" />
                <span>SIGNOUT</span>
              </button>
            </div>
          )}

          {/* Quick theme adjuster */}
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-lg border transition-all duration-200 cursor-pointer ${
              theme === 'amber' ? 'border-amber-500/30 text-[#f59e0b] bg-amber-950/20' : 'border-cyan-500/30 text-[#00f0ff] bg-cyan-950/20'
            }`}
            title="Toggle System Ambiance Profile"
          >
            <Paintbrush size={12} />
          </button>

          {/* Secure dynamic balance view if Vault index is initialized */}
          {isVaultUnlocked && (
            <button
              onClick={() => setIsCapitalFlowOpen(!isCapitalFlowOpen)}
              className="px-2.5 py-1.5 rounded-lg border border-emerald-500/20 bg-emerald-950/15 text-emerald-450 hover:bg-emerald-950/35 text-[10px] tracking-wider uppercase font-bold flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <Coins className="w-3 h-3 text-emerald-450 animate-pulse" />
              <span>£{(netfinanceSettlement / 1000000).toFixed(2)}M</span>
            </button>
          )}

          {/* TASK 3: COMPACT COLLAPSIBLE ADVANCED CONSOLE BUTTON */}
          <div className="relative" ref={consoleRef}>
            <button
              onClick={() => {
                setIsAdvancedConsoleOpen(!isAdvancedConsoleOpen);
                setIsNodesDropdownOpen(false);
                setIsCapitalFlowOpen(false);
                setIsHealthOpen(false);
              }}
              className={`flex items-center gap-1.5 px-4 py-2 text-[10.5px] font-bold uppercase tracking-wider rounded-lg border transition-all duration-300 shadow-sm cursor-pointer select-none ${
                isAdvancedConsoleOpen 
                  ? "border-[#e840f3] bg-[#e840f3]/15 text-[#e840f3] shadow-[0_0_15px_rgba(232,64,243,0.3)] font-extrabold"
                  : "border-zinc-800 bg-black/40 text-purple-400 hover:border-purple-550 hover:text-white"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${isAdvancedConsoleOpen ? "bg-[#e840f3] animate-pulse" : "bg-purple-500"}`} />
              NexoraX Advanced Console ▾
            </button>

            {/* TASK 4: GROUPED DROPDOWN CATEGORIES (Desktop absolute dropdown menu overlay) */}
            {isAdvancedConsoleOpen && (
              <div 
                className="absolute right-0 mt-2.5 bg-black/98 border border-purple-500/30 rounded-xl p-5 w-[85vw] max-w-[800px] shadow-[0_20px_50px_rgba(0,0,0,0.95)] z-[1001] border-t-2 border-t-[#e840f3] text-left animate-in fade-in slide-in-from-top-3 duration-250 max-h-[360px] overflow-y-auto custom-scrollbar backdrop-blur-3xl"
              >
                <div className="flex justify-between items-center border-b border-white/5 pb-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Layers className="w-3.5 h-3.5 text-purple-400" />
                    <span className="font-sans font-black uppercase tracking-wider text-[#e840f3] text-xs">
                      NexoraX Cybernetic Console Panel
                    </span>
                  </div>
                  <span className="text-[8px] text-slate-500 uppercase tracking-widest font-mono">
                    Node Configuration Access
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {advancedCategories.map((group, groupIndex) => (
                    <div key={groupIndex} className="flex flex-col gap-2.5">
                      <h5 className="text-[10px] font-extrabold text-[#00ffd2] uppercase tracking-wider border-b border-zinc-900 pb-1 flex items-center gap-1.5 bg-cyan-950/20 px-1.5 rounded">
                        <span className="w-1 h-1 rounded-full bg-cyan-400 shrink-0"></span>
                        {group.title}
                      </h5>
                      <div className="flex flex-col gap-1">
                        {group.items.map((item, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              onNavItemClick(mapCategoryItemToTabName(item));
                            }}
                            className="text-left text-zinc-450 hover:text-[#e840f3] hover:bg-white/5 px-2 py-1.5 rounded text-[10px] uppercase font-bold transition-all truncate border border-transparent hover:border-zinc-800/40 cursor-pointer font-sans"
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Hamburger menu toggle button */}
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden shrink-0 text-cyan-400 hover:text-white p-2 border border-cyan-500/20 rounded-lg bg-cyan-900/10 transition-all cursor-pointer select-none"
          aria-label="Toggle structural console panel"
        >
          {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* TASK 2: HORIZONTALLY SCROLLABLE QUICK TAGS SUB-BAR (Target Height: 48-56px) */}
      <div className="hidden lg:block w-full max-w-full min-w-0 border-t border-b border-white/5 bg-[#030612]/90 sticky top-[72px] lg:top-[84px] z-45">
        <div className="w-full max-w-7xl min-w-0 mx-auto px-3 sm:px-6 py-2.5 flex items-center justify-between gap-2 sm:gap-4 select-none">
          
          {/* Horizontally scrollable quick-access sub row */}
          <div className="hidden lg:flex flex-1 min-w-0 overflow-x-auto whitespace-nowrap scroll-smooth py-0.5 custom-scrollbar items-center gap-1.5 sm:gap-2 mr-4">
            <span className="text-[8px] font-extrabold tracking-widest text-[#00ffd2] uppercase font-mono mr-1.5 shrink-0 select-none">
              Quick:
            </span>
            {[
              { label: "Home", tab: "Home" },
              { label: "Nexus OS", tab: "Nexus OS" },
              { label: "Operator Console", tab: "Operator Console" },
              { label: "Latest News", tab: "Latest News" },
              { label: "Updates", tab: "Updates" },
              { label: "Vision", tab: "Vision" },
              { label: "Team", tab: "Team" },
              { label: "Payment Tiers", tab: "Tiers" },
              { label: "Points", tab: "Points" },
              { label: "Contact", tab: "Contact" }
            ].map((tag) => {
              const worksAsActive = activeTab === tag.tab;
              return (
                <button
                  key={tag.label}
                  onClick={() => onNavItemClick(tag.tab)}
                  className={`px-3 py-1 text-[9px] font-bold tracking-wider uppercase rounded-full transition-all duration-200 cursor-pointer border select-none inline-block ${
                    worksAsActive 
                      ? "border-[#00c3ff]/40 bg-[#00c3ff]/15 text-[#00c3ff]" 
                      : "border-zinc-800/60 bg-black/35 text-slate-400 hover:text-white hover:border-cyan-500/20"
                  }`}
                >
                  {tag.label}
                </button>
              );
            })}
          </div>

          {/* TASK 10: ACTIVE PAGE / CURRENT VIEW STATUS INDICATOR */}
          {activeTab && (
            <div className="hidden md:flex items-center shrink-0 pr-3 border-r border-white/10 select-none">
              <span className="text-[8.5px] text-cyan-400 uppercase font-black tracking-widest bg-cyan-950/30 px-2 py-0.5 border border-cyan-800/35 rounded-md flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-cyan-400 animate-ping"></span>
                ACTIVE: {activeTab === "Tiers" ? "Payment Tiers" : activeTab}
              </span>
            </div>
          )}

          {/* TASK 7: COMPACT ACTIVE NODE MATRIX DROPDOWN CONTAINER */}
          <div className="relative w-full min-w-0 lg:w-auto lg:shrink-0 select-none" ref={matrixDropdownRef}>
            <button
              onClick={() => {
                setIsNodesDropdownOpen(!isNodesDropdownOpen);
                setIsAdvancedConsoleOpen(false);
              }}
              className="tab-button w-full lg:w-auto min-h-[44px] lg:min-h-0 justify-center px-3.5 py-1.5 rounded-full border border-cyan-500/25 bg-cyan-950/20 text-[9px] text-cyan-400 hover:border-cyan-400 uppercase font-black tracking-wider flex items-center gap-1.5 cursor-pointer transition-all"
            >
              <Cpu className="w-3 h-3 text-cyan-500 animate-pulse" />
              <span className="lg:hidden">Browse Categories ▾</span><span className="hidden lg:inline">Category Tabs with Drop Down to Sub-Categories ▾</span>
            </button>

            {isNodesDropdownOpen && (
              <div 
                className="absolute right-0 mt-2 bg-black/98 border border-cyan-500/30 rounded-xl p-3 lg:p-4 w-[calc(100vw-24px)] max-w-72 lg:w-72 shadow-[0_15px_40px_rgba(0,0,0,0.9)] z-[1002] border-t-2 border-t-cyan-500 text-left max-h-[360px] overflow-y-auto custom-scrollbar backdrop-blur-3xl transition-all"
              >
                <div className="text-[8px] font-black tracking-widest border-b border-cyan-950 pb-1.5 mb-2 text-slate-500 uppercase">
                  CATEGORY SECTORS AND SUB-CATEGORIES
                </div>
                
                <div className="space-y-1.5">
                  {visibleCategoryTabs.map((category) => {
                    const isGroupExpanded = activeNodeGroup === category.title;
                    return (
                      <div key={category.id} className="border border-zinc-900 rounded-lg overflow-hidden bg-zinc-950/25">
                        <button
                          onClick={() => setActiveNodeGroup(isGroupExpanded ? null : category.title)}
                          className="tab-button w-full text-left px-2.5 py-1.5 text-[9px] font-black uppercase text-slate-300 flex justify-between items-center transition-colors font-mono cursor-pointer"
                          style={{ background: 'rgba(0,0,0,0.4)', borderRadius: '0px', border: 'none' }}
                        >
                          <span className="truncate">{category.title} ({category.nodes.length})</span>
                          {isGroupExpanded ? <ChevronUp size={10} className="shrink-0 ml-1" /> : <ChevronDown size={10} className="shrink-0 ml-1" />}
                        </button>

                        {isGroupExpanded && (
                          <div className="p-1.5 text-[9px] bg-black/70 flex flex-col gap-1 max-h-48 overflow-y-auto custom-scrollbar border-t border-zinc-900 text-left">
                            {category.nodes.map((nodeName, nodeIdx) => (
                              <button
                                key={nodeIdx}
                                onClick={() => {
                                  handleNodeClick({
                                    name: nodeName,
                                    categoryTitle: category.title,
                                    categoryId: category.id
                                  });
                                }}
                                className="tab-button w-full text-left px-2 py-2 rounded text-slate-350 hover:text-[#00ffd2] transition-all font-mono leading-relaxed truncate block text-[9.5px] cursor-pointer"
                                style={{ background: 'transparent', border: 'none', margin: '0px', textAlign: 'left', display: 'block', width: '100%', minHeight: '32px' }}
                              >
                                <span className="text-cyan-500 font-extrabold mr-1.5 shrink-0">#{category.id.toString().padStart(3, '0')}</span>
                                <span className="tracking-wide select-none">{nodeName}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Render selected node small active badge next to node trigger on desktop */}
          {selectedNodeName && (
            <div className="hidden lg:flex items-center gap-1.5 bg-purple-950/25 border border-purple-500/20 rounded-full px-2.5 py-1 text-[8.5px] uppercase font-bold text-slate-300">
              <Activity size={10} className="text-purple-400" />
              <span className="truncate max-w-[120px]">Node: {selectedNodeName}</span>
              <button 
                onClick={() => { setSelectedNodeName(null); onLocalTabChange(null); }} 
                className="text-slate-500 hover:text-white cursor-pointer ml-1 text-[9px] font-bold"
                title="Disconnect node"
              >
                ✕
              </button>
            </div>
          )}

        </div>
      </div>

      {/* MOBILE EXPANDED DRAWER MENU SECTION (Task 8 & 9) */}
      {isMobileMenuOpen && (
        <div className="lg:hidden w-full min-w-0 max-w-full box-border bg-[#02040b] border-t border-cyan-500/20 flex flex-col px-4 py-4 gap-3 max-h-[70vh] overflow-y-auto custom-scrollbar pb-8 relative z-55">
          
          {/* Mobile Search Input */}
          <div className="relative w-full flex items-center mb-1">
            <Search className="w-3.5 h-3.5 text-cyan-500/40 absolute left-3" />
            <input
              type="text"
              placeholder="SEARCH SECURE LEDGERS..."
              value={globalSearch}
              onChange={(e) => setGlobalSearch?.(e.target.value)}
              className="w-full bg-black/40 border border-cyan-955/50 rounded-lg pl-9 py-2 text-[10px] text-white tracking-widest focus:outline-none focus:border-cyan-400 text-left uppercase font-mono"
            />
          </div>

          {/* Active Navigation Indicator inside Drawer (Task 10) */}
          {activeTab && (
            <div className="p-2 border border-cyan-500/20 rounded-lg bg-cyan-950/15 text-center text-[10px] font-black uppercase tracking-widest text-[#00f0ff] select-none">
              Currently Mapped: {activeTab === "Tiers" ? "Payment Tiers" : activeTab}
            </div>
          )}

          {/* Mobile Navigation List (Simple main public pages + compact Category structure) */}
          <div className="flex flex-col gap-1.5 pt-1.5">
            <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider mb-1 px-1">PUBLIC PORTALS:</span>
            {[
              { label: "Home", tab: "Home" },
              { label: "Services", tab: "Services / Solutions" },
              { label: "Marketplace", tab: "Marketplace" },
              { label: "Digital Products", tab: "Digital Products" },
              { label: "Product Builder", tab: "Product Builder" },
              { label: "My Projects", tab: "My Projects" },
              { label: "My Purchases", tab: "My Purchases" },
              { label: "Nexus OS", tab: "Nexus OS" },
              { label: "Operator Console", tab: "Operator Console" },
              { label: "Remote Workplace", tab: "Remote Workplace" },
              { label: "Smart Quote", tab: "Smart Quote" },
              { label: "Client Preview", tab: "Client Preview" },
              { label: "Showcase", tab: "Showcase" },
              { label: "QR Campaign", tab: "QR Campaign" },
              { label: "Launch Pad", tab: "Launch Pad" },
              { label: "Guides & Videos", tab: "Guides & Videos" },
              { label: "Remote Training", tab: "Remote Training" },
              { label: "Pricing", tab: "Tiers" },
              { label: "Contact", tab: "Contact" },
              ...(authUser 
                ? [
                    ...(isDirector ? [{ label: "Admin Panel", tab: "Admin Panel" }] : []),
                    { label: "Log Out", tab: "LOG_OUT", isLogout: true }
                  ] 
                : [
                    { label: "Login / Join", tab: "Login / Join" }
                  ]
              )
            ].map((item) => {
              const isItemActive = activeTab === item.tab;
              const isLogoutItem = 'isLogout' in item;
              return (
                <button
                  key={`mobile-${item.label}`}
                  onClick={() => onNavItemClick(item.tab)}
                  className={`w-full text-left py-2 px-3.5 rounded-lg border font-mono text-[10px] font-bold uppercase tracking-wider transition-all min-h-[44px] flex items-center cursor-pointer ${
                    isLogoutItem
                      ? "bg-rose-955/25 border-rose-500/30 text-rose-400 hover:bg-rose-900/45 hover:border-rose-400"
                      : isItemActive 
                        ? "bg-cyan-950/45 border-cyan-500/45 text-cyan-400" 
                        : "bg-transparent border-transparent text-slate-400 hover:text-white"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* Mobile Collapsible Advanced Console Category groups inside hamburger */}
          <div className="border-t border-white/5 pt-3">
            <span className="text-[8px] text-[#e840f3] font-bold uppercase tracking-wider mb-2 block px-1">
              NEXORAX ADVANCED GROUPS:
            </span>
            <div className="space-y-2">
              {advancedCategories.map((group, groupIdx) => (
                <div key={groupIdx} className="border border-purple-500/10 rounded-lg p-2 bg-purple-950/5">
                  <div className="text-[8.5px] font-extrabold uppercase tracking-wide text-[#00ffd2] mb-1 px-1">
                    {group.title}
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {group.items.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => onNavItemClick(mapCategoryItemToTabName(item))}
                        className="text-left text-zinc-400 hover:text-white px-2 py-1.5 rounded bg-black/25 text-[8px] uppercase font-bold truncate min-h-[38px] flex items-center transition-all cursor-pointer font-sans"
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Login & Join buttons */}
          <div className="flex flex-col gap-2 pt-3 border-t border-white/5">
            {!user && onShowGovCorpGateway && (
              <button
                onClick={() => { onShowGovCorpGateway(); setIsMobileMenuOpen(false); }}
                className="w-full text-center text-black bg-gradient-to-r from-amber-500 to-amber-600 font-extrabold py-2.5 rounded-lg text-[9px] tracking-widest uppercase cursor-pointer min-h-[44px] flex items-center justify-center transition-all"
              >
                GOV/CORP GATEWAY
              </button>
            )}

            {user ? (
              <button
                onClick={async () => { await handleHeaderLogout?.(); setIsMobileMenuOpen(false); }}
                className="w-full text-center text-rose-400 border border-rose-500/20 rounded-lg py-2.5 bg-rose-950/10 text-[9px] tracking-widest font-bold uppercase cursor-pointer min-h-[44px] flex items-center justify-center transition-all"
              >
                LOGOUT
              </button>
            ) : (
              <button
                onClick={() => { onLocalTabChange?.("Login"); setIsMobileMenuOpen(false); }}
                className="w-full text-center text-cyan-400 border border-cyan-500/25 rounded-lg py-2.5 bg-cyan-950/10 text-[9px] tracking-widest font-bold uppercase cursor-pointer min-h-[44px] flex items-center justify-center transition-all"
              >
                LOGIN
              </button>
            )}

            <button
              onClick={() => { onLocalTabChange?.("Join"); setIsMobileMenuOpen(false); }}
              className="w-full text-center text-[#e840f3] border border-[#e840f3]/40 rounded-lg py-2.5 text-[9px] tracking-widest font-extrabold uppercase cursor-pointer min-h-[44px] flex items-center justify-center transition-all"
            >
              JOIN UNIT
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
